import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Card,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui';
import {
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  CheckCircle,
  ChevronLeft,
} from 'lucide-react';

interface SubCategory {
  id: string;
  name: string;
  description: string;
  priority: 'Mission Critical' | 'Important' | 'Moderate';
  aiScore: number;
  aiRationale: string;
  userScore?: number;
  userRationale?: string;
  approved: boolean;
  missingData?: boolean; // Flag for items that can't be answered
}

interface Category {
  id: string;
  name: string;
  priority: 'Mission Critical' | 'Important' | 'Moderate';
  expanded: boolean;
  subcategories: SubCategory[];
}

const ReviewerEvaluation = () => {
  const navigate = useNavigate();
  const [selectedDocument, setSelectedDocument] = useState('technical-proposal');
  const [activeTab, setActiveTab] = useState('technical');

  // Use submission info if available, otherwise fallback to mock data
  const vendorName = 'Aetheris Navigation and Perception';

  const [technicalCategories, setTechnicalCategories] = useState<Category[]>([
    {
      id: 'flight-performance',
      name: 'Flight Performance',
      priority: 'Mission Critical',
      expanded: true,
      subcategories: [
        {
          id: 'range-endurance',
          name: 'Range & Endurance',
          description: 'Maximum flight distance and duration on single charge/tank',
          priority: 'Mission Critical',
          aiScore: 5,
          aiRationale:
            'Aetheris NP-300 offers 450km range and 8-hour endurance, exceeding the 400km minimum requirement. Battery technology is proven with multiple successful field deployments.',
          approved: true,
        },
        {
          id: 'speed-altitude',
          name: 'Speed & Altitude',
          description: 'Maximum operational speed and altitude ceiling',
          priority: 'Important',
          aiScore: 4,
          aiRationale:
            'Top speed of 180 km/h and service ceiling of 6,000m significantly exceeds requirements. Advanced aerodynamics provide superior performance in varied conditions.',
          approved: true,
        },
        {
          id: 'weather-resistance',
          name: 'Weather Resistance',
          description: 'Ability to operate in adverse weather conditions',
          priority: 'Important',
          aiScore: 3,
          aiRationale:
            'IP65 rating and operational in winds up to 45 km/h. However, ice protection system is optional and may require additional investment for all-weather operations.',
          userScore: 4,
          userRationale:
            'After review, the base weather capabilities meet our needs. Ice protection can be added for specific missions if needed.',
          approved: false,
        },
      ],
    },
    {
      id: 'payload-sensors',
      name: 'Payload & Sensors',
      priority: 'Mission Critical',
      expanded: false,
      subcategories: [
        {
          id: 'max-payload',
          name: 'Maximum Payload',
          description: 'Total weight capacity for sensors and equipment',
          priority: 'Mission Critical',
          aiScore: 4,
          aiRationale:
            '15kg payload capacity accommodates all required sensor packages with 3kg margin. Modular mounting system allows flexible configuration.',
          approved: true,
        },
        {
          id: 'sensor-integration',
          name: 'Sensor Integration',
          description: 'Compatibility with various sensor types (cameras, LIDAR, etc.)',
          priority: 'Important',
          aiScore: 0,
          aiRationale: 'Unable to assess - vendor submission does not include sensor integration specifications.',
          approved: false,
          missingData: true,
        },
      ],
    },
  ]);

  const toggleCategory = (categoryId: string, scorecard: string) => {
    if (scorecard === 'technical') {
      setTechnicalCategories((cats) =>
        cats.map((cat) => (cat.id === categoryId ? { ...cat, expanded: !cat.expanded } : cat))
      );
    }
    // Add similar logic for other scorecards
  };

  const updateScore = (categoryId: string, subcategoryId: string, score: number, scorecard: string) => {
    if (scorecard === 'technical') {
      setTechnicalCategories((cats) =>
        cats.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                subcategories: cat.subcategories.map((sub) =>
                  sub.id === subcategoryId ? { ...sub, userScore: score, approved: false } : sub
                ),
              }
            : cat
        )
      );
    }
  };

  const updateRationale = (categoryId: string, subcategoryId: string, rationale: string, scorecard: string) => {
    if (scorecard === 'technical') {
      setTechnicalCategories((cats) =>
        cats.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                subcategories: cat.subcategories.map((sub) =>
                  sub.id === subcategoryId ? { ...sub, userRationale: rationale, approved: false } : sub
                ),
              }
            : cat
        )
      );
    }
  };

  const approveAIScore = (categoryId: string, subcategoryId: string, scorecard: string) => {
    if (scorecard === 'technical') {
      setTechnicalCategories((cats) =>
        cats.map((cat) =>
          cat.id === categoryId
            ? {
                ...cat,
                subcategories: cat.subcategories.map((sub) =>
                  sub.id === subcategoryId
                    ? { ...sub, approved: true, userScore: undefined, userRationale: undefined }
                    : sub
                ),
              }
            : cat
        )
      );
    }
  };

  const totalItems = technicalCategories.reduce((sum, cat) => sum + cat.subcategories.length, 0);
  const reviewedItems = technicalCategories.reduce(
    (sum, cat) => sum + cat.subcategories.filter((sub) => sub.approved || sub.userScore).length,
    0
  );

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Mission Critical':
        return 'bg-red-900/20 text-red-400 border border-red-800';
      case 'Important':
        return 'bg-blue-900/20 text-blue-400 border border-blue-800';
      default:
        return 'bg-slate-700 text-slate-300 border border-slate-600';
    }
  };

  const renderScorecard = (categories: Category[], scorecardName: string) => (
    <div className="space-y-4">
      {categories.map((category) => (
        <Collapsible
          key={category.id}
          open={category.expanded}
          onOpenChange={() => toggleCategory(category.id, scorecardName)}
        >
          <Card className="bg-white dark:bg-slate-900 border-border">
            <CollapsibleTrigger className="w-full">
              <div className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {category.expanded ? (
                    <ChevronDown className="w-5 h-5 text-muted-foreground dark:text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-muted-foreground dark:text-muted-foreground" />
                  )}
                  <span className="text-foreground font-medium">{category.name}</span>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${getPriorityColor(category.priority)}`}>
                  {category.priority}
                </span>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 pb-4 space-y-3">
                {category.subcategories.map((subcategory) => (
                  <div
                    key={subcategory.id}
                    className="bg-muted rounded-lg border border-border p-4"
                  >
                    {/* Subcategory Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-foreground font-medium">{subcategory.name}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded ${getPriorityColor(subcategory.priority)}`}>
                            {subcategory.priority}
                          </span>
                          {subcategory.missingData && (
                            <div className="flex items-center gap-1 text-warning text-xs">
                              <AlertTriangle className="w-4 h-4" />
                              <span>Missing Data</span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{subcategory.description}</p>
                      </div>
                    </div>

                    {/* AI Score */}
                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 mb-3 border border-border">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm text-card-foreground">AI Assessment</span>
                        {!subcategory.missingData && (
                          <div className="ml-auto flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <div
                                key={star}
                                className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium ${
                                  star <= subcategory.aiScore
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-200 dark:bg-slate-700 text-muted-foreground'
                                }`}
                              >
                                {star}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-card-foreground">{subcategory.aiRationale}</p>
                      {!subcategory.approved && !subcategory.userScore && !subcategory.missingData && (
                        <button
                          onClick={() => approveAIScore(category.id, subcategory.id, scorecardName)}
                          className="mt-2 text-sm text-primary hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          Approve AI Score
                        </button>
                      )}
                    </div>

                    {/* User Score */}
                    {(subcategory.userScore || (!subcategory.approved && !subcategory.missingData)) && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-card-foreground">Your Score:</span>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                onClick={() => updateScore(category.id, subcategory.id, star, scorecardName)}
                                className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium transition-colors ${
                                  star <= (subcategory.userScore || 0)
                                    ? 'bg-green-600 text-white'
                                    : 'bg-slate-200 dark:bg-slate-700 text-muted-foreground hover:bg-slate-300 dark:hover:bg-slate-600'
                                }`}
                              >
                                {star}
                              </button>
                            ))}
                          </div>
                        </div>
                        <textarea
                          placeholder="Your rationale..."
                          value={subcategory.userRationale || ''}
                          onChange={(e) => updateRationale(category.id, subcategory.id, e.target.value, scorecardName)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg p-3 text-foreground placeholder:text-slate-500 dark:placeholder:text-slate-500 min-h-[80px] focus:outline-none focus:border-blue-500"
                        />
                      </div>
                    )}

                    {subcategory.approved && (
                      <div className="flex items-center gap-2 text-sm text-success">
                        <CheckCircle className="w-4 h-4" />
                        Approved
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Second Top Nav - Back Button */}
      <div className="bg-white dark:bg-slate-900 border-b border-border px-6 py-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/review/tasks')}
            className="flex items-center gap-2 text-muted-foreground hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            All Tasks
          </button>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <h2 className="text-foreground font-medium">{vendorName}</h2>
          <div className="ml-auto text-sm text-muted-foreground">
            <span className="text-primary">{reviewedItems}</span> / {totalItems} items reviewed
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Scorecard Panel (Left/Middle) */}
        <div className="w-1/2 border-r border-border flex flex-col">
          <div className="p-6 overflow-y-auto flex-1">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white dark:bg-slate-900 border-b border-border rounded-none w-full justify-start h-auto p-0 mb-6">
                <TabsTrigger
                  value="technical"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-100 pb-3 pt-3 px-4"
                >
                  Technical
                </TabsTrigger>
                <TabsTrigger
                  value="manufacturing"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-100 pb-3 pt-3 px-4"
                >
                  Manufacturing
                </TabsTrigger>
                <TabsTrigger
                  value="vc"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-100 pb-3 pt-3 px-4"
                >
                  VC Scorecard
                </TabsTrigger>
                <TabsTrigger
                  value="dod"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none text-muted-foreground data-[state=active]:text-slate-900 dark:data-[state=active]:text-slate-100 pb-3 pt-3 px-4"
                >
                  DoW Readiness
                </TabsTrigger>
              </TabsList>

              <TabsContent value="technical" className="mt-0">
                {renderScorecard(technicalCategories, 'technical')}
              </TabsContent>
              <TabsContent value="manufacturing" className="mt-0">
                <div className="text-center py-12 text-muted-foreground">
                  Manufacturing scorecard coming soon...
                </div>
              </TabsContent>
              <TabsContent value="vc" className="mt-0">
                <div className="text-center py-12 text-muted-foreground">
                  VC scorecard coming soon...
                </div>
              </TabsContent>
              <TabsContent value="dod" className="mt-0">
                <div className="text-center py-12 text-muted-foreground">
                  DoW readiness scorecard coming soon...
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Document Viewer (Right) */}
        <div className="w-1/2 flex flex-col">
          <div className="bg-white dark:bg-slate-900 border-b border-border p-4">
            <Select value={selectedDocument} onValueChange={setSelectedDocument}>
              <SelectTrigger className="bg-card border-slate-300 dark:border-slate-700 text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="technical-proposal">Technical Proposal - NP-300.pdf</SelectItem>
                <SelectItem value="performance-data">Performance Data Sheet.pdf</SelectItem>
                <SelectItem value="certifications">Certifications & Compliance.pdf</SelectItem>
                <SelectItem value="pricing">Pricing & Terms.pdf</SelectItem>
                <SelectItem value="case-studies">Case Studies.pdf</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 overflow-y-auto bg-muted p-8">
            <div className="bg-white rounded-lg shadow-lg min-h-[1000px] p-8">
              {/* Simulated PDF Content */}
              <div className="space-y-6 text-gray-800">
                <h1 className="text-3xl font-bold">NP-300 Technical Proposal</h1>
                <div className="border-b-2 border-gray-200 pb-2">
                  <p className="text-gray-600">{vendorName}</p>
                </div>

                <section>
                  <h2 className="text-xl font-semibold mb-3">1. Executive Summary</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The NP-300 represents our latest generation fixed-wing autonomous drone platform,
                    designed specifically for long-range reconnaissance and surveillance missions.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold mb-3">2. Technical Specifications</h2>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-medium">Maximum Range:</p>
                        <p className="text-gray-700">450 km</p>
                      </div>
                      <div>
                        <p className="font-medium">Endurance:</p>
                        <p className="text-gray-700">8 hours</p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Final Actions Bar */}
      <div className="bg-white dark:bg-slate-900 border-t border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/review/tasks')}>
              Save Draft
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20"
            >
              Request More Information
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              className="bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30 border-red-300 dark:border-red-800"
            >
              Reject & Pass
            </Button>
            <Button variant="secondary" size="sm">
              Schedule Call
            </Button>
            <Button variant="default" size="sm" className="bg-green-600 hover:bg-green-500">
              Approve & Advance
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewerEvaluation;

